// Domain types shared by the UI, the mock API, and the real API client.
// Mirrors product-spec.md Section 9 and DESIGN.md Section 10.

export type FeedbackStatus =
  | 'submitted'
  | 'under_review'
  | 'awaiting_info'
  | 'in_progress'
  | 'resolved'
  | 'confirmed'
  | 'reopened';

export interface TimelineEntry {
  status: FeedbackStatus;
  at: string; // ISO 8601
  note?: string;
}

export interface Attachment {
  id: string;
  name: string; // sanitized original name
  size: number; // bytes
  mime: string;
}

export interface LuoAnswer {
  body: string; // rendered as escaped text, never raw HTML (AC-B-004)
  at: string; // ISO 8601
}

export interface Feedback {
  id: string;
  summary: string;
  detail: string;
  status: FeedbackStatus;
  org: string;
  project: string;
  createdAt: string;
  updatedAt: string;
  timeline: TimelineEntry[];
  luoAnswer?: LuoAnswer;
  attachments: Attachment[];
}

export interface SessionHandle {
  org: string;
  project: string;
  expiresAt: string; // non-sensitive; the raw invite token is NEVER stored here
}

export interface CooperationInput {
  name: string;
  contact: string;
  company: string;
  projectType: string;
  summary: string;
  detail: string;
  budget: string;
  timeframe: string;
  consent: boolean;
  website: string;
  files: { name: string; size: number; type: string; blob?: Blob }[];
}

export interface CooperationResponse {
  status: 'submitted';
}

export type SessionError = 'invalid' | 'expired' | 'revoked';

export interface ApiError {
  error: { code: string; message: string; requestId: string };
}

export const STATUS_LABEL: Record<FeedbackStatus, string> = {
  submitted: '已提交',
  under_review: '审核中',
  awaiting_info: '待补充',
  in_progress: '处理中',
  resolved: '已解决',
  confirmed: '已确认',
  reopened: '已重新打开',
};

// State-to-action matrix (DESIGN.md 10.8) - the binding contract for AC-B-020/021.
// Legal customer-driven transitions only; everything else returns 409.
export const LEGAL_TRANSITIONS: Record<FeedbackStatus, FeedbackStatus[]> = {
  submitted: [],
  under_review: [],
  awaiting_info: ['under_review'], // customer replies -> back to review
  in_progress: [],
  resolved: ['confirmed', 'reopened'], // confirm (terminal) or reopen
  confirmed: [], // terminal
  reopened: [],
};
